import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { get as _get } from "lodash";
import { HighChart } from "../../../../../resusableComponents/common/formElements";

const CHART_COLORS = ["#FA6284", "#59C8EA", "#FFDE59", "#1A1A1A", "#CCCCCC", "#E6E6E6"];

// Normalise glow_class values coming from the API to CSS modifier names
const GLOW_CLASS_MAP = { pink: "pink", aqua: "aqua", gold: "gold", blue: "aqua" };

class DashboardView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      audienceType: "levels",
    };
  }

  _getAssetChartOptions = () => {
    const assetData = _get(this.props, "reportData.sections.dashboard.asset_performance", []);

    return {
      chart: { type: "bar", height: 300 },
      title: { text: null },
      xAxis: { categories: assetData.map((d) => d.label) },
      yAxis: { min: 0, title: { text: null } },
      series: [{ name: "% Share of MQLs", data: assetData.map((d) => d.percent), color: "#FA6284", borderRadius: 4 }],
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: { valueSuffix: "%" },
    };
  };

  _getAudienceChartOptions = () => {
    const { audienceType } = this.state;
    const dataKey = audienceType === "levels" ? "job_levels" : "job_functions";
    const data = _get(this.props, `reportData.sections.dashboard.${dataKey}`, []);

    return {
      chart: { type: "pie", height: 250 },
      title: { text: null },
      plotOptions: {
        pie: {
          innerSize: "65%",
          dataLabels: { enabled: false },
          showInLegend: true,
          borderWidth: 2,
          borderColor: "#ffffff",
        },
      },
      series: [{
        name: "Share",
        colorByPoint: true,
        data: data.map((d, i) => ({ name: d.label, y: d.percent, color: CHART_COLORS[i % CHART_COLORS.length] })),
      }],
      credits: { enabled: false },
      legend: { enabled: true, layout: "vertical", align: "right", verticalAlign: "middle" },
    };
  };

  _getIndustryChartOptions = () => {
    const industryData = _get(this.props, "reportData.sections.dashboard.lead_volume_by_sector", []);

    return {
      chart: { type: "bar", height: 250 },
      title: { text: null },
      xAxis: { categories: industryData.map((d) => d.label) },
      yAxis: { min: 0, title: { text: null } },
      series: [{ name: "%", data: industryData.map((d) => d.percent), color: "#59C8EA", borderRadius: 4 }],
      credits: { enabled: false },
      legend: { enabled: false },
    };
  };

  render() {
    const { audienceType } = this.state;
    const accounts = _get(this.props, "reportData.sections.dashboard.top_accounts", []);
    const kpiCards = _get(this.props, "reportData.sections.dashboard.kpi_cards", []);
    const totalLeads = _get(this.props, "reportData.total_leads", "");

    return (
      <div className="sa-tab-content">
        {/* Section heading */}
        <div className="mb-5">
          <h2 className="sa-section-title">
            <FormattedMessage id="strategic_analysis.view_report.dashboard.title" defaultMessage="Lead Acquisition Breakdown" />
          </h2>
          <p className="sa-section-desc">
            <FormattedMessage
              id="strategic_analysis.view_report.dashboard.desc"
              defaultMessage="Firmographic, behavioral, and psychographic analysis of the {total_leads} MQLs generated in Q2. Review these insights to align sales outreach with specific intent signals."
              values={{ total_leads: totalLeads }}
            />
          </p>
        </div>

        {/* Row 1: Asset Performance + Buying Groups */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="sa-card">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="sa-card__title">
                    <FormattedMessage id="strategic_analysis.view_report.dashboard.asset_perf" defaultMessage="Asset Performance" />
                  </h3>
                  <p className="sa-card__subtitle">
                    <FormattedMessage id="strategic_analysis.view_report.dashboard.asset_perf_sub" defaultMessage="% share of total MQLs generated" />
                  </p>
                </div>
                <span className="sa-kpi-card__pivot-badge">% Share</span>
              </div>
              <div className="sa-chart-area">
                <HighChart options={this._getAssetChartOptions()} />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="sa-card">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="sa-card__title">
                    <FormattedMessage id="strategic_analysis.view_report.dashboard.buying_groups" defaultMessage="Buying Groups" />
                  </h3>
                  <p className="sa-card__subtitle">
                    <FormattedMessage id="strategic_analysis.view_report.dashboard.buying_groups_sub" defaultMessage="Demographic breakdown" />
                  </p>
                </div>
              </div>
              <div className="sa-toggle-bar">
                <button
                  id="sa-audience-toggle-levels"
                  type="button"
                  className={`sa-toggle-btn${audienceType === "levels" ? " sa-toggle-btn--active" : ""}`}
                  onClick={() => this.setState({ audienceType: "levels" })}
                >
                  <FormattedMessage id="strategic_analysis.view_report.dashboard.job_level" defaultMessage="Job Level" />
                </button>
                <button
                  id="sa-audience-toggle-function"
                  type="button"
                  className={`sa-toggle-btn${audienceType === "function" ? " sa-toggle-btn--active" : ""}`}
                  onClick={() => this.setState({ audienceType: "function" })}
                >
                  <FormattedMessage id="strategic_analysis.view_report.dashboard.job_function" defaultMessage="Job Function" />
                </button>
              </div>
              <div className="sa-chart-area--sm">
                <HighChart options={this._getAudienceChartOptions()} />
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Accounts table + Industry chart */}
        <div className="row g-4 mb-4">
          <div className="col-lg-8">
            <div className="sa-card">
              <h3 className="sa-card__title mb-4">
                <FormattedMessage id="strategic_analysis.view_report.dashboard.top_accounts" defaultMessage="Top Engaged Target Accounts" />
              </h3>
              <div className="table-responsive">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th><FormattedMessage id="strategic_analysis.view_report.dashboard.col_company" defaultMessage="Company" /></th>
                      <th><FormattedMessage id="strategic_analysis.view_report.dashboard.col_leads" defaultMessage="Total Leads" /></th>
                      <th><FormattedMessage id="strategic_analysis.view_report.dashboard.col_personas" defaultMessage="Key Personas" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((acc, index) => (
                      <tr key={`${acc.name}-${index}`}>
                        <td className="sa-table__company">{acc.name}</td>
                        <td className="sa-table__num">{acc.leads}</td>
                        <td>
                          {(acc.badges || []).map((b, bIdx) => (
                            <span key={`${b}-${bIdx}`} className="sa-table__badge">{b}</span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="sa-card">
              <h3 className="sa-card__title mb-4">
                <FormattedMessage id="strategic_analysis.view_report.dashboard.vertical" defaultMessage="Vertical Penetration" />
              </h3>
              <div className="sa-chart-area--sm">
                <HighChart options={this._getIndustryChartOptions()} />
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: KPI cards */}
        <div className="row g-4">
          {kpiCards.map((kpi, index) => {
            const rawGlow = _get(kpi, "glow_class", "");
            const glowMod = GLOW_CLASS_MAP[rawGlow] || rawGlow;
            const salesAction = _get(kpi, "sales_action", "");
            return (
              <div key={`kpi-${index}`} className="col-md-4">
                <div className="sa-kpi-card">
                  <div className={`sa-kpi-card__glow sa-kpi-card__glow--${glowMod}`} />
                  <h3 className="sa-kpi-card__title">{_get(kpi, "title", "")}</h3>
                  <div className="sa-kpi-card__value">{_get(kpi, "data_point", "")}</div>
                  <p className="sa-kpi-card__desc">{_get(kpi, "sub_text", "")}</p>
                  {salesAction && (
                    <div className="sa-kpi-card__sales-action">
                      <span className={`sa-kpi-card__sales-action-label sa-kpi-card__sales-action-label--${glowMod}`}>
                        <FormattedMessage
                          id="strategic_analysis.view_report.dashboard.sales_action"
                          defaultMessage="Sales Action"
                        />
                      </span>
                      <p className="sa-kpi-card__sales-action-text">{salesAction}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default DashboardView;
